import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Send, Calendar, MapPin, Users, DollarSign, Loader, CheckCircle, Mail, Phone, User, MessageSquare } from 'lucide-react';
import Navigation from './Navigation';

function BulkContactForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [excludedCategories, setExcludedCategories] = useState([]);

  const [formData, setFormData] = useState({
    couple_name: '',
    couple_email: '',
    couple_phone: '',
    wedding_date: '',
    venue_location: '',
    guest_count: '',
    budget_range: '',
    message: ''
  });

  const budgetRanges = [
    { label: '$ - Budget Friendly', value: 'budget' },
    { label: '$$ - Moderate', value: 'moderate' },
    { label: '$$$ - Upper Range', value: 'upper' },
    { label: '$$$$ - Premium', value: 'premium' },
    { label: '$$$$$ - Luxury', value: 'luxury' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load categories
      const categoriesResponse = await fetch('https://api.bridalconnect.co.za/api/categories');
      let loadedCategories = [];
      if (categoriesResponse.ok) {
        const data = await categoriesResponse.json();
        loadedCategories = data.categories || [];
        setCategories(loadedCategories);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load categories');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCategoryToggle = (categoryId) => {
    if (excludedCategories.includes(categoryId)) {
      return; // Can't select excluded categories
    }

    setSelectedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleSelectAll = () => {
    const selectableCategories = categories
      .filter(cat => !excludedCategories.includes(cat.id))
      .map(cat => cat.id);

    if (selectedCategories.length === selectableCategories.length) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(selectableCategories);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    if (selectedCategories.length === 0) {
      setError('Please select at least one category');
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://api.bridalconnect.co.za/api/enquiries/bulk-contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          selected_category_ids: selectedCategories,
          excluded_category_ids: excludedCategories
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit enquiry');
      }

      const result = await response.json();

      setSuccess({
        message: result.message,
        providers_contacted: result.providers_contacted,
        emails_sent: result.emails_sent,
        categories: result.categories,
        breakdown: result.breakdown
      });

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Error submitting bulk enquiry:', err);
      setError(err.message || 'Failed to submit enquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-purple-100">
        <Navigation />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading form...</p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-purple-100">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Enquiries Sent Successfully! 🎉</h2>
              <p className="text-lg text-gray-600 mb-6">
                Your enquiry has been sent to <span className="font-bold text-purple-600">{success.providers_contacted} vendors</span> across {success.categories.length} categories.
              </p>

              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <h3 className="font-semibold text-gray-800 mb-4">Breakdown by Category:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {success.breakdown.map((item, index) => (
                    <div key={index} className="flex items-center justify-between bg-white rounded-lg p-3">
                      <span className="font-medium text-gray-700">{item.category}</span>
                      <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                        {item.provider_count} {item.provider_count === 1 ? 'vendor' : 'vendors'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-blue-800 text-sm">
                  <strong>What happens next?</strong> Vendors will review your enquiry and respond directly to your email: <strong>{formData.couple_email}</strong>
                </p>
              </div>

              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium hover:shadow-lg transition"
                >
                  Go to Dashboard
                </button>
                <button
                  onClick={() => {
                    setSuccess(null);
                    setSelectedCategories([]);
                    setFormData(prev => ({ ...prev, message: '' }));
                  }}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition"
                >
                  Send Another Enquiry
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-purple-100">
      <Navigation />

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <div className="text-center mb-6">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Contact Multiple Vendors</h1>
            <p className="text-lg text-gray-600">
              Send your wedding details to multiple vendors at once and compare quotes
            </p>
          </div>

        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg">
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Category Selection */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Select Categories</h2>
            <p className="text-gray-600 mb-6">Choose which types of vendors you want to contact</p>

            <div className="mb-4">
              <button
                type="button"
                onClick={handleSelectAll}
                className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg font-medium hover:bg-purple-200 transition"
              >
                {selectedCategories.length === categories.filter(c => !excludedCategories.includes(c.id)).length
                  ? 'Deselect All'
                  : 'Select All'}
              </button>
              <span className="ml-4 text-sm text-gray-600">
                {selectedCategories.length} categor{selectedCategories.length === 1 ? 'y' : 'ies'} selected
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {categories.map(category => {
                const isExcluded = excludedCategories.includes(category.id);
                const isSelected = selectedCategories.includes(category.id);

                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => handleCategoryToggle(category.id)}
                    disabled={isExcluded}
                    className={`p-4 rounded-lg border-2 font-medium text-sm transition ${
                      isExcluded
                        ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                        : isSelected
                        ? 'bg-purple-600 border-purple-600 text-white'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-purple-400'
                    }`}
                  >
                    {category.name}
                    {isExcluded && (
                      <span className="block text-xs mt-1">Already booked</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Your Details */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Your Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Couple Name *
                </label>
                <input
                  type="text"
                  name="couple_name"
                  value={formData.couple_name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="John & Jane"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email Address *
                </label>
                <input
                  type="email"
                  name="couple_email"
                  value={formData.couple_email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="couple_phone"
                  value={formData.couple_phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="+27 82 123 4567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Wedding Date *
                </label>
                <input
                  type="date"
                  name="wedding_date"
                  value={formData.wedding_date}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Wedding Details */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Wedding Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Venue Location *
                </label>
                <input
                  type="text"
                  name="venue_location"
                  value={formData.venue_location}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Cape Town, Western Cape"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Guest Count *
                </label>
                <input
                  type="number"
                  name="guest_count"
                  value={formData.guest_count}
                  onChange={handleChange}
                  required
                  min="1"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="100"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Overall Budget Range
                </label>
                <select
                  name="budget_range"
                  value={formData.budget_range}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select budget range (optional)</option>
                  {budgetRanges.map(range => (
                    <option key={range.value} value={range.value}>{range.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Message */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Additional Message</h2>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Tell vendors about your vision
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Share any special requirements, themes, or questions you have for vendors..."
            />
          </div>

          {/* Submit Button */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex-1 px-6 py-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-xl transition font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Sending Enquiries...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send to {selectedCategories.length} Categor{selectedCategories.length === 1 ? 'y' : 'ies'}
                  </>
                )}
              </button>
            </div>

            <p className="text-sm text-gray-500 text-center mt-4">
              * Required fields
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

export default BulkContactForm;
